<template>
  <v-file-input
      :id="control.uniqueId"
      :name="control.name || control.uniqueId"
      :accept="control.allowedExtension"
      outlined
      :placeholder="control.placeholderText"
      clearable
      v-model="fileModel"
      show-size
      truncate-length="30"
      :disabled="isDisabled"
      :rules="fileRules"
      ref="fileInput"
      :persistent-hint="!!fileHint"
      :hint="fileHint"
      @change="toBase64(fileModel)"

  >
    <template v-slot:selection="{ index, text }">
      <v-chip
          v-if="index < 2"
          color="deep-purple accent-4"
          dark
          label
          small
      >
        {{ selectedFileLabel || text }}
      </v-chip>

      <span
          v-else-if="index === 2"
          class="overline grey--text text--darken-3 mx-2"
      >
        +{{ fileModel.length - 2 }} File(s)
      </span>
    </template>
    <template v-if="!fileModel && selectedFileLabel" v-slot:prepend-inner>
      <v-chip
          color="deep-purple accent-4"
          dark
          label
          small
      >
        {{ selectedFileLabel }}
      </v-chip>
    </template>
    <template v-slot:append>
        <pdf-preview-dialog
          v-if="formFileID"
          style="margin: -8px!important;"
          :pdf-data-id="formFileID"
        />
    </template>
  </v-file-input>
</template>

<script>
import {BaseControlSkeleton} from 'v-form-builder';
import {commitAddNotification} from '@/store/main/mutations';
import {rules} from '@/utils';
import {readUserProfile} from '@/store/main/getters';
import PdfPreviewDialog from "@/components/dialogs/PdfPreviewDialog.vue";

export default {
  name: 'FileUploadControl',
  components: {PdfPreviewDialog},
  extends: BaseControlSkeleton,
  data: () => ({
    fileModel: null,
    maxFileSize: 100,
    rulesLocal: rules,
    isDisabled: false,
    formFileID: 0,
    displayValue: null,
    fileValidationError: '',
  }),
  async mounted() {
    this.displayValue = this.value;
    this.formFileID = this.value?.form_files_id
    this.maxFileSize = this.control.maxSize;
    this.clearInvalidFile(this.value);

    if (this.control.disableByRoleSetting) {
      const userProfile = readUserProfile(this.$store);
      this.isDisabled =
          !(userProfile?.role?.settings?.permissions?.can_edit_control_field?.methods?.can_edit_control_field);
    } else if (this.control.isDisabled) {
      this.isDisabled = true;
    }

    // if (!this.value) {
    //   await Promise.all([
    //     await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
    //     this.updateValue(this.value),
    //     this.fileModel = this.value || {},
    //   ]);
    // }
  },
  watch: {
    value(newValue) {
      this.displayValue = newValue;
      this.formFileID = newValue?.form_files_id || 0;
      if (!newValue) {
        this.fileModel = null;
      } else {
        this.clearInvalidFile(newValue);
      }
    },
  },
  computed: {
    fileRules() {
      return this.rulesLocal.maxSizeFile(this.control.maxSize).concat([
        () => !this.fileValidationError || this.fileValidationError,
      ]);
    },
    selectedFileLabel() {
      return this.fileModel?.name || this.displayValue?.name || '';
    },
    fileHint() {
      if (!this.displayValue || typeof this.displayValue !== 'object') {
        return '';
      }
      const parts = [];
      if (this.displayValue.original_size) {
        parts.push(this.formatBytes(this.displayValue.original_size));
      }
      if (this.displayValue.upload_status === 'pending') {
        parts.push('upload pendente');
      } else if (this.displayValue.form_files_id) {
        parts.push(`arquivo #${this.displayValue.form_files_id}`);
      }
      return parts.join(' - ');
    },
  },
  methods: {
    clearFile() {
      const fileInput = this.$refs.fileInput;
      if (fileInput && typeof fileInput.clearableCallback === 'function') {
        fileInput.clearableCallback();
      }
      this.fileModel = null;
      this.displayValue = null;
      this.formFileID = 0;
      this.updateValue(null);
    },
    clearInvalidFile(value) {
      const maxSize = Number(this.control.maxSize);
      const fileSize = value?.size ?? value?.original_size;
      if (!value || maxSize === 0 || fileSize <= maxSize * 1024) {
        return false;
      }

      this.fileValidationError = `Este campo deve ser menor do que ${this.control.maxSize}Kb`;
      this.clearFile();
      this.$nextTick(() => this.$refs.fileInput?.validate());
      return true;
    },
    formatBytes(value) {
      if (!value) {
        return '';
      }
      const units = ['B', 'KB', 'MB', 'GB'];
      let size = Number(value);
      let index = 0;
      while (size >= 1024 && index < units.length - 1) {
        size = size / 1024;
        index += 1;
      }
      return `${size.toFixed(index ? 1 : 0)} ${units[index]}`;
    },
    async toBase64(value) {

      let fileInfos = '';
      if (!value) {
        this.fileValidationError = '';
        this.clearFile();
        return;
      }

      if (value instanceof Blob) {
        if (Number(this.control.maxSize) !== 0 && (value.size / 1024 / this.control.maxSize) > 1) {
          commitAddNotification(this.$store, {
            content: `O arquivo excedeu o limite máximo de ${this.control.maxSize}Kb.`,
            color: 'error',
          });
          this.fileValidationError = `Este campo deve ser menor do que ${this.control.maxSize}Kb`;
          this.clearFile();
          this.$nextTick(() => this.$refs.fileInput?.validate());
          return;
        }

        if (value.type !== this.control.allowedExtension) {
          commitAddNotification(this.$store, {
            content: `Tipo inválido! são aceitos apenas arquivos no formato ${this.control.allowedExtension.split('/')[1]}`,
            color: 'error',
          });
          this.fileValidationError = '';
          this.clearFile();
          return;
        }
        this.fileValidationError = '';
        /* tslint:disable:no-bitwise */
        fileInfos = {
          name: value.name,
          type: value.type,
          original_size: value.size,
          file: value,
          control_id: this.control.name || this.control.uniqueId,
          upload_status: 'pending',
          form_files_id: this?.value?.form_files_id | 0,
          idempotency_key: `${this.control.name || this.control.uniqueId}-${value.name}-${value.size}-${value.lastModified}`,
        };
        this.displayValue = fileInfos;
        this.formFileID = fileInfos.form_files_id || 0;
        /* tslint:disable:no-bitwise */
      }
      this.updateValue(fileInfos);
    },
  },
};
</script>

<style scoped>
</style>
